import { matchRoutes, type RouteObject } from "react-router-dom";

import {
  RESERVED_NON_COMPANY_SUBDOMAINS,
  RETURN_PATH_EXCLUSIONS,
} from "../constants";
import { localStorageHelpers, ProfileStorage } from "./browser-storge";
import { Log } from "./log.helpers";

let registeredAppRoutes: RouteObject[] | null = null;

export const registerAppRoutes = (routes: RouteObject[]): void => {
  registeredAppRoutes = routes;
};

export const isValidAppPath = (rawPath: string | null | undefined): boolean => {
  if (!rawPath?.startsWith("/")) {
    return false;
  }

  const pathOnly = rawPath.split("?")[0];
  const pathLower = pathOnly.toLowerCase();

  if (RETURN_PATH_EXCLUSIONS.some(exclusion => pathLower.includes(exclusion))) {
    return false;
  }

  if (!registeredAppRoutes) {
    // Routes not registered yet — be permissive rather than blocking auth flow.
    return true;
  }

  const matches = matchRoutes(registeredAppRoutes, pathOnly);
  if (!matches || matches.length === 0) {
    return false;
  }

  // matchRoutes returns the catch-all "*" route (NotFoundPage) for unknown paths.
  // Reject when only the wildcard matched.
  return matches.some(m => m.route.path && m.route.path !== "*");
};

export const getAppHost = (): string => {
  return window.location.host;
};

export const isLocalHostMode = (): boolean => {
  return window.location.host.includes("localhost:");
};

export const isUiStagingDomain = (): boolean => {
  return (
    window.location.host.includes("ui-staging") ||
    window.location.host.includes("admin-staging")
  );
};

export const isLandingUiStagingDomain = (): boolean => {
  return window.location.host.includes("land-staging");
};

export const getBaseUrl = (): string => {
  const origin = window.location.origin;
  const pathName = window.location.pathname;

  if (pathName === "/") {
    return window.location.origin;
  }

  return origin + pathName;
};

/**
 * The app's current route pathname, read straight from the hash router without subscribing to it.
 * For the rare non-render caller that needs the route but must not hold a router subscription
 * (e.g. a hook instantiated by many components); inside components prefer `useLocation().pathname`.
 */
export const getCurrentRouterPathname = (): string => {
  const hash = window.location.hash || "";
  const pathnameWithQuery = hash.startsWith("#") ? hash.slice(1) : hash;

  return pathnameWithQuery.split("?")[0] ?? "";
};

export const getCompanySubDomain = (): string | undefined => {
  const host = window.location.host;

  if (!host?.includes(".") || isLocalHostMode()) {
    return undefined;
  }

  const subdomain = host.split(".")[0];

  if (!subdomain) {
    /* handle case of dot at the beginning of domain  */
    return undefined;
  }

  Log.info(`Subdomain found ${subdomain}`);

  return subdomain;
};

export const isReservedNonCompanySubDomain = (
  subdomain: string | undefined,
): boolean => {
  return (
    !!subdomain &&
    RESERVED_NON_COMPANY_SUBDOMAINS.includes(subdomain.toLowerCase())
  );
};

/**
 * The subdomain when it can be used as a company code, otherwise `undefined`.
 * `app` / `admin` serve every tenant, so their labels are not company codes — passing one
 * to `/auth/login-saml?companyCode=` matches no company and the request fails.
 * Callers that need the raw host label (test/staging detection) use `getCompanySubDomain`.
 */
export const resolveCompanyCodeFromSubDomain = (
  subdomain: string | undefined,
): string | undefined => {
  if (!subdomain || isReservedNonCompanySubDomain(subdomain)) {
    return undefined;
  }

  return subdomain;
};

export const getCompanyCodeSubDomain = (): string | undefined => {
  return resolveCompanyCodeFromSubDomain(getCompanySubDomain());
};

export const normalizeUrlBasePath = (urlPath: string): string => {
  if (urlPath.endsWith("/")) {
    return urlPath.slice(0, -1);
  }

  return urlPath;
};

export const ensureTrailingSlash = (url: string): string => {
  if (url.endsWith("/")) {
    return url;
  }

  return `${url}/`;
};

export const loginUrlRedirect = (): void => {
  /*
  RYDE-5269- old system logout removed, left if needed
   if (ProfileStorage.isHybridMode) {
    window.location.href = "/default.asp";
  } else {
    window.location.href = "#/login";
  } */
  window.location.href = `#/login`;
};

export const storeCurrentPageAndRedirectToLogin = (): void => {
  const currentHash = window.location.hash || "#/";
  const hashNormalized = (
    currentHash.startsWith("#") ? currentHash.slice(1) : currentHash
  ).toLowerCase();

  if (hashNormalized && isValidAppPath(hashNormalized)) {
    Log.log("Login redirect with storing last path", hashNormalized);
    localStorageHelpers.setItem("returnTo", hashNormalized);
  } else {
    Log.log("Login redirect without storing default", hashNormalized);
    localStorageHelpers.setItem("returnTo", null);
  }

  loginUrlRedirect();
};

const logoutOldSystem = async (): Promise<void> => {
  try {
    fetch("/default.asp?action=logout", { credentials: "include" });
  } catch (error) {
    Log.warn("Error logging out old system, ", error);
  }
};

export const logoutUrlAction = async (): Promise<void> => {
  /*
  RYDE-5269- old system logout removed, left if needed
  cleanup of token in localStorage and cookie done previously by code before this function
  if (ProfileStorage.isHybridMode) {
    window.location.href = "/default.asp?action=logout";
  } else {
    window.location.href = "#/login";
  } */
  if (ProfileStorage.isHybridMode) {
    logoutOldSystem();
  }

  window.location.href = "#/login";
};
